/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-16 11:51:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { TextStyle, SkeletonBodyText, SkeletonDisplayText, Collapsible } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import SearchPreview from 'src/components/views/test-your-search/features/search-preview';
import MerchandisingPreview from 'src/components/views/test-your-search/features/merchandising/merchandising-preview';
import { buildFeaturesPreview } from 'src/helpers/search-helper';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import history from 'src/routes/history';

import infoMinorIcon from 'src/assets/images/common/info-minor-icon.png';
import chevronDownIcon from 'src/assets/images/common/chevron-down-minor.png';
import chevronUpIcon from 'src/assets/images/common/chevron-up-minor.png';

import { ComposedPropsFeatures } from './index.type';
import * as FilterDataActions from 'actions/filter-data-actions';

// Render component
const FeaturePreview = (props: ComposedPropsFeatures) => {
  const {
    i18n,
    query,
    showLoading,
    testSearchData,
    searchFeatures,
    moneyFormat,
    optionsList,
    appLanguage,
    getProductAttribute,
    enablePPR
  } = props;
  // Count rules apply by search
  const features =
    query && !_.isEmpty(query)
      ? buildFeaturesPreview(testSearchData, searchFeatures, i18n)
      : {
          merchandising: [],
          search: [],
          count: 0
        };
  const countFeatures = features.count;
  const [open, setOpen] = useState(true);

  useEffect(() => {
    getProductAttribute();
  }, []);

  if (!showLoading && countFeatures === 0) return null;
  return (
    <div
      className={`Polaris-DataTable TestYourSearch_Table ${
        !open ? 'Polaris-DataTable TestYourSearch_Table--close' : ''
      }`}>
      {showLoading ? (
        <div style={{ padding: '16px', marginTop: '16px' }}>
          <div style={{ marginBottom: '8px' }}>
            <SkeletonDisplayText />
          </div>
          <SkeletonBodyText />
          <SkeletonBodyText />
        </div>
      ) : (
        <>
          <div
            className={`description-collapse ${!open ? 'description-close' : ''}`}
            onClick={() => setOpen(!open)}>
            <div>
              <img alt="pfs-info-minor-icon" src={infoMinorIcon} width={16} height={16} />
              <p>
                {i18n.translate('AdminHTML.TestYourSearch.Table.description', {
                  count: countFeatures,
                  number_rules: (
                    <TextStyle variation="strong">
                      {i18n.translate(`AdminHTML.Common.Duration.rule`, {
                        count: countFeatures,
                        nt_number: i18n.formatNumber(countFeatures)
                      })}
                    </TextStyle>
                  )
                })}
              </p>
            </div>
            <div className="collapse">
              <p>
                {i18n.translate(
                  `AdminHTML.TestYourSearch.TestYourSearch.${open ? 'collapse' : 'showDetail'}`
                )}
              </p>
              <img
                alt="pfs-chevron-icon"
                src={open ? chevronUpIcon : chevronDownIcon}
                width={16}
                height={16}
              />
            </div>
          </div>
          <Collapsible
            open={open}
            id="basic-collapsible"
            transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
            expandOnPrint>
            <div
              className="Polaris-DataTable__ScrollContainer"
              style={{ marginLeft: 0, maxHeight: '402px' }}>
              <table className="Polaris-DataTable__Table">
                <thead>
                  <tr>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.TestYourSearch.Table.column01_Label')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.TestYourSearch.Table.column02_Label')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.TestYourSearch.Table.column03_Label')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ height: '12px' }} />
                  <SearchPreview
                    query={query}
                    searchFeatures={_.get(features, 'search', [])}
                    history={history}
                    enablePPR={enablePPR}
                  />
                  <tr style={{ height: '12px' }} />
                  <MerchandisingPreview
                    appLanguage={appLanguage}
                    optionsList={optionsList}
                    moneyFormat={moneyFormat}
                    history={history}
                    merchandising={_.get(features, 'merchandising', [])}
                  />
                </tbody>
              </table>
            </div>
          </Collapsible>
        </>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get available collections and data
  getProductAttribute: () => dispatch(FilterDataActions.getProductAttribute(ownProps.i18n))
});
const mapStateToProps = state => {
  const testSearchData = _.get(state.product, 'productData.test_search_data', {});
  const searchFeatures = _.get(state.searchPreview, 'features', {});
  const enablePPR = _.get(state.productPerformanceRanking, 'settings.enabled') || false;

  return {
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    optionsList: _.get(state.filterData.productAttributes, 'optionsList', []),
    moneyFormat: _.get(state.oauth.tenantApp, 'externalInfo.money_format', ''),
    enablePPR,
    searchFeatures,
    testSearchData
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(FeaturePreview));
